import { useEffect, useRef, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, setCachedOrgId } from '@/integrations/supabase/client';
import {
  AuthContext,
  type AuthContextType,
  type OrgIdStatus,
  type SignupMetadata,
} from '@/hooks/useAuth';
import { syncRegistrationProfile } from '@/lib/registrationProfile';
import { hasManagerAccessRole, type AccessRoleRow } from '@/lib/accessRoles';
import { PRIMARY_OPERATIONS_ORG_ID } from '@/lib/organization';

type OrgMemberRow = {
  org_id: string | null;
  role: string | null;
  created_at: string | null;
};

type ActiveOrgCandidate = {
  org_id: string;
  role: string | null;
  created_at: string | null;
  hasManagerAccessRole: boolean;
};

const managerRoles = new Set([
  'super_admin',
  'admin',
  'campaign_manager',
  'bud',
  'business_unit_director',
  'supervisor',
  'line_manager',
  'finance_viewer',
  'finance',
]);

const hasManagerMembership = (row: OrgMemberRow) => managerRoles.has(String(row.role));

const resolveActiveOrgId = (rows: OrgMemberRow[], accessRoleRows: AccessRoleRow[] = []) => {
  const candidatesByOrg = new Map<string, ActiveOrgCandidate>();

  rows
    .filter((row) => row.org_id)
    .forEach((row) => {
      candidatesByOrg.set(String(row.org_id), {
        org_id: String(row.org_id),
        role: row.role,
        created_at: row.created_at,
        hasManagerAccessRole: false,
      });
    });

  accessRoleRows
    .filter((row) => row.org_id && hasManagerAccessRole(row))
    .forEach((row) => {
      const orgId = String(row.org_id);
      const candidate = candidatesByOrg.get(orgId) ?? {
        org_id: orgId,
        role: null,
        created_at: null,
        hasManagerAccessRole: false,
      };
      candidate.hasManagerAccessRole = true;
      candidatesByOrg.set(orgId, candidate);
    });

  const candidates = Array.from(candidatesByOrg.values());
  if (!candidates.length) return null;

  // M2M previously accumulated empty duplicate organization records. Prefer the
  // workforce organization whenever the signed-in user belongs to it.
  if (candidatesByOrg.has(PRIMARY_OPERATIONS_ORG_ID)) {
    return PRIMARY_OPERATIONS_ORG_ID;
  }

  const sortedMemberships = candidates.sort((left, right) => {
    if (left.hasManagerAccessRole !== right.hasManagerAccessRole) {
      return left.hasManagerAccessRole ? -1 : 1;
    }

    const leftManager = managerRoles.has(String(left.role));
    const rightManager = managerRoles.has(String(right.role));

    if (leftManager !== rightManager) {
      return leftManager ? -1 : 1;
    }

    const leftCreatedAt = Date.parse(left.created_at ?? '');
    const rightCreatedAt = Date.parse(right.created_at ?? '');

    if (Number.isFinite(leftCreatedAt) && Number.isFinite(rightCreatedAt)) {
      if (leftCreatedAt !== rightCreatedAt) {
        return rightCreatedAt - leftCreatedAt;
      }
    } else if (Number.isFinite(leftCreatedAt)) {
      return -1;
    } else if (Number.isFinite(rightCreatedAt)) {
      return 1;
    }

    return String(left.org_id).localeCompare(String(right.org_id));
  });

  return sortedMemberships[0]?.org_id ?? null;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgIdStatus, setOrgIdStatus] = useState<OrgIdStatus>('idle');
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    let authEventReceived = false;

    const applySession = (nextSession: Session | null) => {
      setSession(nextSession);
      const nextUser = nextSession?.user ?? null;
      const nextUserId = nextUser?.id ?? null;
      const isSameUser = nextUserId && currentUserIdRef.current === nextUserId;
      setUser(nextUser);

      if (!nextUser) {
        currentUserIdRef.current = null;
        setCachedOrgId(null);
        setOrgId(null);
        setOrgIdStatus('idle');
        return;
      }

      if (isSameUser) return;

      currentUserIdRef.current = nextUser.id;
      setOrgId(null);
      setOrgIdStatus('loading');
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!active) return;

      // A slow getSession() can resolve with the pre-login session after SIGNED_IN.
      // Mark the event first so that stale hydration result cannot clear this new session.
      authEventReceived = true;
      applySession(newSession);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!active || authEventReceived) return;

      applySession(currentSession);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const userId = user?.id ?? null;

    if (!userId) {
      setCachedOrgId(null);
      setOrgId(null);
      setOrgIdStatus('idle');
      return;
    }

    let active = true;
    setOrgId(null);
    setOrgIdStatus('loading');

    supabase
      .from('org_members')
      .select('org_id, role, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .then(async ({ data, error }) => {
        if (!active) return;

        if (error) {
          console.error('Failed to fetch org_id:', error);
          setCachedOrgId(null);
          setOrgIdStatus('error');
          return;
        }

        const rows = (data as OrgMemberRow[]) || [];
        if (!rows.length) {
          console.warn('User has no org membership');
          setCachedOrgId(null);
          setOrgId(null);
          setOrgIdStatus('no-membership');
          return;
        }

        let accessRoleRows: AccessRoleRow[] = [];
        if (!rows.some((row) => row.org_id && hasManagerMembership(row))) {
          const { data: accessRoleData, error: accessRoleError } = await (supabase as any)
            .from('user_roles')
            .select('org_id, roles(name, is_super_admin)')
            .eq('user_id', userId)
            .order('org_id', { ascending: true });

          if (accessRoleError) {
            console.warn('Failed to fetch user access roles:', accessRoleError);
          } else {
            accessRoleRows = (accessRoleData as AccessRoleRow[]) || [];
          }
        }

        const resolvedOrgId = resolveActiveOrgId(rows, accessRoleRows);
        if (!resolvedOrgId) {
          setCachedOrgId(null);
          setOrgId(null);
          setOrgIdStatus('no-membership');
          return;
        }

        setCachedOrgId(resolvedOrgId);
        setOrgId(resolvedOrgId);
        setOrgIdStatus('ready');
      })
      .catch((err) => {
        if (!active) return;
        console.error('Unexpected error fetching org_id:', err);
        setCachedOrgId(null);
        setOrgId(null);
        setOrgIdStatus('error');
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  const signIn: AuthContextType['signIn'] = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (!error && data.user) {
      try {
        await syncRegistrationProfile(data.user);
      } catch (syncError) {
        console.error('Failed to sync registration details after sign-in:', syncError);
      }
    }

    return { error };
  };

  const signUp: AuthContextType['signUp'] = async (email, password, metadata: SignupMetadata) => {
    const normalizedEmail = email.trim().toLowerCase();

    // Resend reports permanent bounces asynchronously. Check the suppression list before
    // creating another Auth record so a known-undeliverable address is corrected first.
    const { data: emailSuppressed, error: suppressionLookupError } = await (supabase as any).rpc(
      'is_promoter_email_suppressed',
      { p_email: normalizedEmail },
    );

    if (suppressionLookupError) {
      // Do not make an otherwise healthy signup depend on this optional lookup. The database
      // trigger and Resend webhook remain the final server-side guard for races and retries.
      console.warn('Unable to check promoter email delivery status:', suppressionLookupError);
    }

    if (emailSuppressed === true) {
      return {
        error: {
          code: 'email_address_bounced',
          message: 'EMAIL_ADDRESS_BOUNCED',
          status: 422,
        },
        user: null,
      };
    }

    // Send promoters back to the login screen after verification. A confirmation link can be
    // opened in a different browser from the registration browser, so it must not rely on an
    // in-memory registration session being present.
    const redirectUrl = `${window.location.origin}/ba/login?emailConfirmed=1`;
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { ...metadata, signup_source: 'promoter_registration' },
      },
    });
    return { error, user: data.user };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, orgId, orgIdStatus, signIn, signUp, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
};
